import React, { useEffect, useState } from 'react';
import { Card, CardTitle, Metric, Tag, Button } from '../components/UI';
import { getDashboardMetrics, getQueue, getLoadBoard } from '../api';
import { useAuth } from '../context/AuthContext';
import { UpgradeInline } from '../components/UpgradeGate';
import { Sparkles, Loader2, TrendingUp, Truck, Link2, Lock } from 'lucide-react';

const METRIC_LABELS = {
  active_shipments: 'Active shipments',
  projected_savings: 'Projected savings',
  empty_miles_avoided: 'Empty miles avoided',
  on_time_forecast: 'On-time forecast',
};

export default function Dashboard({ onNavigate }) {
  const { user } = useAuth();
  const isPro = !!(user?.effective_pro || user?.is_pro);
  const [metrics, setMetrics] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loadBoard, setLoadBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const tasks = [getDashboardMetrics(), getQueue()];
        if (isPro) tasks.push(getLoadBoard());
        const results = await Promise.all(tasks);
        if (!active) return;
        setMetrics(results[0]);
        setQueue(results[1]);
        if (isPro) setLoadBoard(results[2]);
      } catch (e) {
        console.error('Dashboard load error', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isPro]);

  const metricItems = metrics
    ? [
        { label: METRIC_LABELS.active_shipments, value: String(metrics.active_shipments) },
        { label: METRIC_LABELS.projected_savings, value: metrics.projected_savings },
        { label: METRIC_LABELS.empty_miles_avoided, value: metrics.empty_miles_avoided.toLocaleString() },
        { label: METRIC_LABELS.on_time_forecast, value: metrics.on_time_forecast },
      ]
    : [];

  return (
    <div className="space-y-[18px]">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-[10px] p-3.5 animate-pulse h-[70px]"
                style={{ background: '#fafbfc', border: '1px solid var(--lf-line)' }}
              />
            ))
          : metricItems.map((m) => <Metric key={m.label} label={m.label} value={m.value} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[18px]">
        <Card>
          <CardTitle>Today's optimization queue</CardTitle>
          {loading ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--lf-muted)' }}>
              <Loader2 className="animate-spin" size={14} /> Loading shipments…
            </div>
          ) : queue.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--lf-muted)' }}>
              No shipments yet. Create one in Optimize Shipment.
            </p>
          ) : (
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr>
                  {['Shipment', 'Lane', 'Status'].map((h) => (
                    <th
                      key={h}
                      className="text-left p-3 font-semibold"
                      style={{ borderBottom: '1px solid var(--lf-line)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {queue.map((row) => (
                  <tr key={row.id}>
                    <td className="p-3 font-medium" style={{ borderBottom: '1px solid var(--lf-line)' }}>
                      {row.shipment_code}
                    </td>
                    <td className="p-3" style={{ borderBottom: '1px solid var(--lf-line)' }}>
                      {row.lane}
                    </td>
                    <td className="p-3" style={{ borderBottom: '1px solid var(--lf-line)' }}>
                      <Tag variant={row.status === 'Needs review' ? 'warning' : 'success'}>
                        {row.status}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <CardTitle>Load board matches</CardTitle>
            <TrendingUp size={16} style={{ color: 'var(--lf-blue)' }} />
          </div>
          {!isPro ? (
            <div className="space-y-3">
              <UpgradeInline
                title="Load board is a Pro feature"
                description="Surface consolidation pairings and backhauls that save real money every day."
              />
              <div
                className="p-3 rounded-lg flex items-center gap-2 text-xs"
                style={{ background: '#f8faff', border: '1px dashed var(--lf-line)', color: 'var(--lf-muted)' }}
              >
                <Lock size={14} /> Preview: 4+ pairings per day on real shipments.
              </div>
            </div>
          ) : loading || !loadBoard ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--lf-muted)' }}>
              <Loader2 className="animate-spin" size={14} /> Finding matches…
            </div>
          ) : (
            <>
              <p className="text-sm mb-3" style={{ color: '#475467' }}>
                {loadBoard.summary}
              </p>
              {loadBoard.pairings.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                  No current pairings.
                </p>
              ) : (
                <div className="space-y-2">
                  {loadBoard.pairings.map((p) => (
                    <div
                      key={p.id}
                      className="p-3 rounded-lg"
                      style={{
                        background: '#f8faff',
                        border: '1px solid var(--lf-line)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5">
                          {p.kind === 'empty_truck' ? (
                            <Truck size={13} style={{ color: 'var(--lf-blue)' }} />
                          ) : (
                            <Link2 size={13} style={{ color: 'var(--lf-blue)' }} />
                          )}
                          <b className="text-xs font-semibold">
                            {p.kind === 'empty_truck' ? 'Empty truck pair' : 'Shipment chain'}
                          </b>
                        </div>
                        <b className="text-xs" style={{ color: 'var(--lf-green)' }}>
                          +${p.savings.toLocaleString()}
                        </b>
                      </div>
                      <div className="text-xs mb-1" style={{ color: '#111827' }}>
                        {p.description}
                      </div>
                      <div className="text-[11px]" style={{ color: 'var(--lf-muted)' }}>
                        {p.lane} • {p.empty_miles_saved.toLocaleString()} empty miles saved
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <Button variant="primary" onClick={() => onNavigate && onNavigate('optimize')}>
                  <Sparkles size={15} /> Plan new shipment
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
